import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import PrivateLayout from "../components/layout/PrivateLayout";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { userService } from "../services/api";
import { updateFromProfile } from "../store/slices/authSlice";

// Routes that are accessible to non-KYC verified users
const NON_KYC_ROUTES = [
  "/",
  "/profile",
  "/discovery",
  "/compare",
  "/team-of-the-week",
  "/market",
  "/news",
  "/whats-new",
  "/events",
  "/organizers",
  // Marin decision D28 option a (2026-07-23): non-KYC users may VIEW the
  // lawyers directory (the header submenu already allowlisted it — this fixes
  // the guard mismatch that silently bounced them to "/"). Contact gates are
  // unchanged.
  "/lawyers",
  // Competitions (TP-CompetitionPage build): provider content, viewable
  // without KYC — kept in sync with the header submenu allowlist
  // (useHeaderNavigation.js allowedNonKycRoutes), D28 lesson: BOTH lists.
  "/competitions",
  "/kyc-verification",
];

// Path patterns also accessible without KYC. Needed because dynamic
// segments (e.g. /news/<articleId>) don't match the exact-string list
// above — without this, clicking a news card bounced non-KYC users back
// to "/" with no visible explanation.
//
// Patterns are exact regexes (no startsWith) so future routes under
// /news/ — e.g. /news/admin or /news/draft/<id> — are NOT accidentally
// exposed to non-KYC users. Add explicit entries here if a new
// non-KYC route under /news/ is intentional.
const NON_KYC_ROUTE_PATTERNS = [
  /^\/news\/[^/]+\/?$/, // /news/<articleId>
  /^\/competitions\/[^/]+\/?$/, // /competitions/<competitionId>
];

const isNonKycRoute = (pathname) =>
  NON_KYC_ROUTES.includes(pathname) ||
  NON_KYC_ROUTE_PATTERNS.some((re) => re.test(pathname));

// Optionally accept requiredPermission for permission-level gating
const PrivateRoute = ({ children, allowedRoles, requiredPermission }) => {
  const { user, tokens, kycVerified } = useSelector((state) => state.auth);
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const dispatch = useDispatch();

  // "Permissions unknown" is not "permission denied" (QAF01-061).
  //
  // A restored session carries whatever was written to localStorage at login.
  // Several real paths store the user BEFORE /users/profile/ has answered and
  // therefore leave `permissions` absent entirely: registration
  // (authService.register), staff onboarding (StaffDetailsStep), and any login
  // whose profile enrichment threw (the catch in _handleAuthSuccessPayload is
  // silent). Nothing re-hydrates permissions on a cold start except the home
  // dashboard (useHomeDashboard.syncProfileAccessData) — which a deep link
  // never mounts. The gate below then read "absent" as "right is missing" and
  // bounced a fully entitled user to "/", silently, on every bookmark or
  // emailed link.
  //
  // absent (not an array) = UNDETERMINED -> resolve once, show a loader
  // present (an array, even empty) = DETERMINED -> enforce, unchanged
  const permissionsUndetermined =
    !!requiredPermission &&
    !!tokens?.access &&
    !!user &&
    !Array.isArray(user.permissions);
  const [resolutionFailed, setResolutionFailed] = useState(false);

  useEffect(() => {
    if (!permissionsUndetermined || resolutionFailed) return undefined;
    let active = true;
    (async () => {
      const res = await userService.getProfile();
      if (!active) return;
      const profile = res?.data || {};
      const permissions =
        profile.permissions ||
        profile?.data?.permissions ||
        profile?.results?.permissions;
      if (res?.success && Array.isArray(permissions)) {
        dispatch(updateFromProfile({ permissions }));
      } else {
        // Fail closed: an unresolvable profile falls back to the strict gate
        // rather than parking the user on a spinner forever.
        setResolutionFailed(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [permissionsUndetermined, resolutionFailed, dispatch]);

  if (!tokens?.access || !user) {
    return isMobile ? (
      <Navigate to="/splash" replace />
    ) : (
      <Navigate to="/login" replace />
    );
  }

  // CLEAN APPROACH: If user has tokens but no roles, redirect to login
  // Login will fetch fresh user data with roles, and if still no roles, login will handle onboarding redirect
  if (!user.roles || user.roles.length === 0) {
    return isMobile ? (
      <Navigate to="/splash" replace />
    ) : (
      <Navigate to="/login" replace />
    );
  }

  // Check KYC verification for routes not in NON_KYC_ROUTES / prefixes.
  if (!kycVerified && !isNonKycRoute(location.pathname)) {
    return <Navigate to="/" replace />;
  }

  // If roles are specified, check role access
  if (allowedRoles && !allowedRoles.some((role) => user.roles.includes(role))) {
    return <Navigate to="/" replace />;
  }

  // Permissions not established yet: wait for the profile to answer instead of
  // treating "unknown" as "denied". Costs nothing for the normal session, whose
  // stored permissions array makes this branch unreachable.
  if (permissionsUndetermined && !resolutionFailed) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-white"
        role="status"
      >
        <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // If a specific permission is required, enforce using profile.permissions
  if (requiredPermission) {
    const userPermissions = Array.isArray(user?.permissions)
      ? user.permissions
      : [];
    if (Array.isArray(requiredPermission)) {
      const hasAny = requiredPermission.some((perm) =>
        userPermissions.includes(perm)
      );
      if (!hasAny) {
        return <Navigate to="/" replace />;
      }
    } else if (typeof requiredPermission === "string") {
      const hasPermission = userPermissions.includes(requiredPermission);
      if (!hasPermission) {
        return <Navigate to="/" replace />;
      }
    }
  }

  return <PrivateLayout>{children}</PrivateLayout>;
};

export default PrivateRoute;
